import { Dispatch, SetStateAction } from 'react';

import { GenericApiResponse } from '@type/general';
import { Sector } from '@type/util';

import { InvestorsTableData } from './hooks/useGetInvestors';
import { TeamMembersTableData } from './hooks/useGetTeamMembers';
import { TimeLineData } from './services/rounds';
import { CompanyStatus } from '@constants/common';
import { SectionMapper } from './constants';
import { FieldValues } from 'react-hook-form';

export type CreateCompanyFormFieldValues = {
  id: string;
  status: string;
  //Legal info
  companyRegisteredName: string;
  cin: string;
  registrationNumber: string;
  incorporationCountry: string;
  currency: string;
  //Address
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  pin: string;
  timezone: string;
  //Brand
  companyBrandName: string;
  website: string;
  companyLogo: { file: File | undefined; path: string };
  companyStage: string;
  sector: string;
  companyOneLiner: string;
  detailedDescription: string;
  companyEmailDomains: string[];
  hexValue: string;
};

export type CreateTeamFormFieldValues = {
  //TeamMember
  teamMemberName: string;
  teamMemberEmail: string | null;
  teamMemberSecondaryEmail: string | null;
  teamMemberLinkedInUrl: string;
  teamMemberDesignation: string | null;
  teamMemberDescription: string;
  teamMemberProfileImage: { file: File | undefined; path: string } | undefined;
  teamMemberRole: string;
  teamMemberExperience: string;
  teamMemberCoverImage: { file: File | undefined; path: string } | undefined;
  teamMemberCreatedAt: string;
  teamMemberOrder: number;
};
export type CreateAngelInvestorFormFieldValues = {
  angelInvestorId: string;
  angelInvestorName: string;
  angelInvestorURL: string | null;
  angelInvestorDesignation: string | null;
  angelInvestorLogo: { file: File | undefined; path: string };
  angelInvestorDescription: string;
  angelInvestorUserId: string;
  angelInvestorVisibleInDeal: boolean;
  angelInvestorCreatedAt: string;
  angelInvestorOrder: number;
};

export type CreateInstituitionalInvestorFieldValues = {
  instituitonalInvestorId: string;
  instituitonalInvestorName: string;
  instituitonalInvestorURL: string;
  instituitonalInvestorLogo: { file: File | undefined; path: string };
  instituitonalInvestorDescription: string | null;
  instituitionalInvestorDesignation: string;
  instituitionalInvestorUserId: string;
  instituitionalInvestorVisibleInDeal: boolean;
  instituitionalInvestorCreatedAt: string;
  instituitionalInvestorOrder: number;
};

export type AddTeamMembersModalPropsType = {
  isOpen: boolean;
  handleCloseModal: () => void;
  memberDetails?: TeamMembersTableData;
  maxOrder: number;
  resetOffset: () => void;
};

export type AddInstituitionalInvestorModalPropsType = {
  isOpen: boolean;
  setModalOpen: Dispatch<SetStateAction<boolean>>;
  handleCloseModal: () => void;
  memberDetails?: InvestorsTableData;
  roundId?: string;
  setIsDeleteModalOpen: Dispatch<SetStateAction<boolean>>;
  disableChooseInvestor?: boolean;
  resetInstitutionalOffset: () => void;
  maxOrder: number;
};

export type AddAngelInvestorModalPropsType = {
  isOpen: boolean;
  setModalOpen: Dispatch<SetStateAction<boolean>>;
  handleCloseModal: () => void;
  memberDetails?: InvestorsTableData;
  roundId?: string;
  setIsDeleteModalOpen: Dispatch<SetStateAction<boolean>>;
  disableChooseInvestor?: boolean;
  resetAngelOffset: () => void;
  maxOrder: number;
};

export type AddProductModalPropType = {
  isOpen: boolean;
  setModalOpen: Dispatch<SetStateAction<boolean>>;
  handleCloseModal: () => void;
};

export type InvestorsTableProps = {
  showVisibleDealColumn: boolean;
  showEmptyState: boolean;
  roundId?: string;
  disableEdit?: boolean;
};
export type AddRoundProps = {
  isOpen: boolean;
  handleCloseModal: () => void;
  valuationType: string;
  isRoundDetailsRequired?: boolean;
  roundDetails?: TimeLineData;
  existingRoundNames?: string[];
  isOpportunity?: boolean;
};

export type AddValuationProps = {
  isOpen: boolean;
  handleCloseModal: () => void;
};

export type CreateValuationForm = {
  convertibleTerms?: string;
  valuation?: string;
  roundDate: string | Date;
};

export type RoundCreateForm = {
  roundName?: string;
  size?: string;
  companyStage?: string;
} & CreateValuationForm;

export type Company = {
  company_id: string;
  company_code: string;
  registered_name: string;
  cin: string;
  registration_number: string;
  incorporated_in: string;
  currency: string;
  address: {
    address_line_1: string;
    address_line_2: string;
    city: string;
    state: string;
    country: string;
    pin: number;
    time_zone: string;
    id: string;
  };
  company_known_name: string;
  website: string;
  email_domains?: {
    domain_name: string;
    id: string;
  }[];
  company_one_liner: string;
  company_description: string;
  hex_value: string;
  icon_url: string;
  sector_and_category: Sector[] | null;
  company_stage: string;
  team_members: null;
  external_investors: null;
  product_details: {
    id: string;
    name: string;
    status: string;
    description: string;
    ios_app_url: string;
    android_app_url: string;
    web_app_url: string;
    assets: {
      id: string;
      path: string;
      asset_type: string;
      fileName: string;
      order: number;
      source: string;
    }[];
  }[];
  competitors: {
    id: string;
    name: string;
    logo_path: string;
  }[];
  previous_valuations: null;
  last_known_valuation: number;
  last_funding_month: string;
  last_funding_year: number;
  total_funding_raised: number;
  status: CompanyStatus;
  terms:
    | {
        id: string;
        term_key: string;
        term_value: string;
        sequence: string;
      }[]
    | null;
};

export type GetCompanyRes = {
  status: string;
  result: Company;
};

export type CreateCompanyReq = {
  registered_name: string;
  cin: string;
};

export type UpdateCompanyReqPayload = {
  registered_name?: string;
  cin?: string;
  registration_number?: string;
  incorporated_in?: string;
  currency?: string;
  company_code?: string;
  address_id?: string;

  company_known_name?: string;
  website?: string;
  company_one_liner?: string;
  company_description?: string;
  hex_value?: string;
  icon_url?: string;
  company_stage?: string;

  last_known_valuation?: number;
  last_funding_month?: string;
  last_funding_year?: number;
  total_funding_raised?: number;

  status?: CompanyStatus;
};

export type UpdateCompanyReq = {
  company_id: string;
  payload: UpdateCompanyReqPayload;
};

export type CreateCompanyAddressReq = {
  company_id: string;
  payload: {
    address_line_1: string;
    address_line_2: string;
    city: string;
    state: string;
    country: string;
    pin?: number;
    time_zone?: string;
  };
};

export type UpdateCompanyAddressReq = {
  company_id: string;
  payload: {
    address_line_1: string;
    address_line_2: string;
    city: string;
    state: string;
    country: string;
    pin?: number;
  };
};

export type CreateCompanyEmailDomainReq = {
  company_id: string;
  payload: {
    domain_name: string;
  };
};
export type CreateCompanyEmailDomainRes = {
  status: string;
  result: string;
};

export type BulkDeleteCompanyEmailDomainsReq = {
  company_id: string;
  email_domain_ids: string[];
};
export type DeleteCompanyEmailDomainRes = {
  status: string;
  result: string;
};

// Team Member
export type TeamMemberListReq = {
  companyId: string;
  payload: {
    search: string;
    limit: number;
    offset: number;
  };
};

export type TeamMemberRes = {
  data: {
    id: string;
    name: string;
    email: string;
    designation: string;
    description: string;
    linkedin_url: string;
    cover_image_path: string;
    secondary_email: string;
    user_id: string;
    role: string;
    profile_image_path: string;
    experience_in_yrs: number;
    created_at: string;
    order: number;
  }[];
  limit: number;
  offset: number;
  max_order: number;
  total_count: number;
};

export type CreateTeamMemberReq = {
  company_id: string;
  payload: {
    name: string;
    email: string | null;
    secondary_email: string | null;
    role: string;
    designation: string | null;
    description: string;
    linkedin_url: string;
    profile_image_path: string;
    cover_image_path: string;
    created_at: string;
    order: number;
  };
};

export type CreateTeamMemberRes = GenericApiResponse<string>;

export type UpdateTeamMemberReq = CreateTeamMemberReq & { member_id: string };

// Investor
export type Investor = {
  id: string;
  name: string;
  designation: string | null;
  description: string | null;
  url: string | null;
  logo_path: string;
  user_id?: string;
  type: ExternalInvestorType;
  round_id?: string;
  is_visible_in_deal: boolean;
  created_at: string;
  order: number;
};

export enum ExternalInvestorType {
  SuperAngelInvestor = 'SuperAngel',
  InstitutionalInvestor = 'InstitutionalInvestor'
}

export type GetInvestorReq = {
  companyId?: string;
  payload: {
    search?: string;
    limit?: number;
    offset?: number;
    roundId?: string;
    type?: ExternalInvestorType;
  };
};
export type GetInvestorRes = {
  status: string;
  result: {
    data: Investor[] | null;
    limit: number;
    offset: number;
    max_order: number;
    total_count: number;
  };
};

export type CreateInvestorReq = {
  companyId: string;
  payload: Omit<Investor, 'id'>;
};
export type CreateInvestorRes = {
  status: string;
  result: Investor;
};

export type UpdateInvestorReq = {
  companyId: string;
  investorId: string;
  payload: Omit<Investor, 'id'>;
};
export type UpdateInvestorRes = GenericApiResponse<Investor>;

export type DeleteInvestorReq = {
  companyId: string;
  investorId: string;
};

export type AddRoundReq = {
  company_id: string;
  payload: {
    round_name: string;
    company_stage: string;
    size: number;
    round_date: string;
    valuation_type: string;
    valuation?: number;
    convertible_debts?: string;
  };
};

export type UpdateRoundReq = AddRoundReq & { round_id: string };

export type AddRoundRes = GenericApiResponse<string>;

export type AddValuationReq = {
  company_id: string;
  payload: {
    round_date: string;
    valuation?: number;
  };
};

export type AddValuationRes = GenericApiResponse<string>;

export type UpdateValuationReq = {
  valuation_id: string;
} & AddValuationReq;

export type GetRoundsRes = {
  valuations: Valuation[] | null;
  rounds: Round[] | null;
};

export type Valuation = {
  id: string;
  round_date: string;
  valuation: number;
};

export type Round = {
  id: string;
  round_name: string;
  company_stage: string;
  size: number;
  round_date: string;
  valuation_type: string;
  valuation: number;
  convertible_Debts: string;
  investor_count: number;
};

export enum RoundType {
  VALUATION_UPDATE = 'VALUATION_UPDATE',
  ROUND = 'ROUND'
}

export type CreateProductFormFieldValues = {
  products: companyProductFieldType[];
};

export type companyProductFieldType = {
  productId: string;
  productName: string;
  productStatus: string;
  productDescription: string;
  iosAppLink: string;
  androidAppLink: string;
  webAppLink: string;
  appScreenshots: {
    id: string;
    file: File | undefined;
    path: string;
    fileName: string;
    order: number;
  }[];
  webScreenshots: {
    id: string;
    file: File | undefined;
    path: string;
    fileName: string;
    order: number;
  }[];
};

export type ProductSectionProps = {
  editMode?: SectionMapper;
  onClickEditButton?: (section: string) => void;
  updateEditMode?: (section?: SectionMapper) => void;
  disableEdit?: boolean;
  getResetProductSection: (formValues: FieldValues) => {};
  getResetFunction: (
    editMode?: SectionMapper
  ) => (formValues: FieldValues) => {};
  companyDetails: Company | undefined;
};

export type CompanyConfiguration = {
  id: string;
  brand_color: string;
};

export type GetCompanyConfigurationRes = {
  status: string;
  result: CompanyConfiguration;
};

export type CreateCompanyConfigurationReq = {
  companyId: string;
  payload: {
    brand_color: string;
  } | null;
};

export type CreateCompanyConfigurationRes = {
  status: string;
  result: CompanyConfiguration;
};

export type UpdateCompanyConfigurationReq = {
  companyId: string;
  configurationId: string;
  payload: {
    brand_color: string;
  };
};

export type UpdateCompanyConfigurationRes = {
  status: string;
  result: CompanyConfiguration;
};

export type CompetitorsSectionPropType = {
  editMode?: SectionMapper;
  onClickEditButton?: (section: string) => void;
  updateEditMode?: (section?: SectionMapper) => void;
  disableEdit?: boolean;
  getResetFunction: (
    editMode?: SectionMapper
  ) => (formValues: FieldValues) => {};
  getResetCompetitors: (formValues: FieldValues) => {};
  companyDetails: Company | undefined;
};

export type createCompetitorFormType = {
  id: string;
  companyName: string;
  companyLogo: { file: File | undefined; path: string };
};

export type CreateCompetitorFormFieldValues = {
  competitors: createCompetitorFormType[];
};

export type AddProductReq = {
  company_id: string;
  payload: productRequestType;
};

export type productRequestType = {
  name: string;
  status: string;
  description: string;
  ios_app_url: string;
  android_app_url: string;
  web_app_url: string;
  assets: {
    id: string;
    path: string;
    asset_type: string;
    source: string;
    order: number;
  }[];
};

export type UpdateProductReq = {
  company_id: string;
  product_id: string;
  payload: {
    name: string;
    status: string;
    description: string;
    ios_app_url: string;
    android_app_url: string;
    web_app_url: string;
  };
};

export type AddProductAssetReq = {
  company_id: string;
  product_id: string;
  payload: {
    path: string;
    asset_type: string;
    order: number;
    source: string;
  };
};

export type deleteProductAssetReq = {
  company_id: string;
  product_id: string;
  asset_id: string;
};

export type addCompetitorReq = {
  company_id: string;
  payload: {
    name: string;
    logo_path: string;
  };
};

export type addCompanyTermsReq = {
  company_id: string;
  payload: {
    terms: {
      id: string;
      term_key: string;
      term_value: string;
      sequence: number;
    }[];
  };
};

export type deleteCompetitorReq = {
  company_id: string;
  competitor_id: string;
};

export type updateCompetitorReq = {
  company_id: string;
  competitor_id: string;
  payload: {
    name: string;
    logo_path: string;
  };
};

export type deleteProductReq = {
  company_id: string;
  product_id: string;
};
